import styled from "@emotion/styled";
import {
  type InstalledWidget,
  type Widget,
  type WidgetSizeDefinition,
} from "@repo/api";
import {
  Button,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  Text,
  Variables,
  pencilIcon,
} from "@repo/skapa";
import { T, t } from "@repo/translation";
import { FlexDiv } from "@repo/ui";

import {
  TemplateSelection,
  useTemplateSelectionActions,
} from "../templates/TemplateSelection";
import { WidgetSettings } from "./settings/WidgetSettings";

const PANEL_WIDTH = "520px";

const InlinePanel = styled(Panel)<{ $isOpen: boolean }>`
  flex: 0 0 auto;
  width: ${({ $isOpen }) => ($isOpen ? PANEL_WIDTH : "0")};
  max-width: 100%;
  overflow: hidden;

  .saleslead__modal-header__title-wrapper {
    margin-inline-start: ${Variables.space100};
  }

  .saleslead__panel__content-wrapper {
    overflow-wrap: anywhere;
  }
`;

const TemplatesFooter = () => {
  const { hasTemplates, isApplying, applyDisabled, onApply, onReset } =
    useTemplateSelectionActions();

  if (!hasTemplates) {
    return null;
  }

  return (
    <PanelFooter renderBorder>
      <Button
        type="primary"
        text={t("action.apply")}
        loading={isApplying}
        disabled={applyDisabled}
        onClick={onApply}
      />
      {onReset && (
        <Button type="secondary" text={t("action.reset")} onClick={onReset} />
      )}
    </PanelFooter>
  );
};

interface CustomizePanelProps {
  isOpen: boolean;
  showTemplates: boolean;
  panelLabel: string;
  selectedWidget?: Widget | InstalledWidget;
  selectedWidgetLabel: string;
  onClose: () => void;
  onRenameWidget: () => void;
  onUpdateWidget: (w: InstalledWidget) => Promise<void>;
  onUpdateWidgetSize: (
    widgetId: number,
    size: WidgetSizeDefinition
  ) => Promise<void>;
}

export const CustomizePanel = ({
  isOpen,
  showTemplates,
  panelLabel,
  selectedWidget,
  selectedWidgetLabel,
  onClose,
  onRenameWidget,
  onUpdateWidget,
  onUpdateWidgetSize,
}: CustomizePanelProps) => {
  return (
    <InlinePanel
      $isOpen={isOpen}
      visible={isOpen}
      alignment="right"
      ariaLabel={panelLabel}
      onClose={onClose}
      header={
        <PanelHeader>
          {showTemplates ? (
            <Text textStyle="Heading.S">
              <T translationKey="label.workspace_templates" />
            </Text>
          ) : (
            <FlexDiv alignItems="center" gap={Variables.space50}>
              <Text textStyle="Heading.S">{selectedWidgetLabel}</Text>
              <Button
                iconOnly
                size="small"
                type="tertiary"
                ssrIcon={pencilIcon}
                text={t("action.change_widget_name")}
                title={t("action.change_widget_name")}
                onClick={onRenameWidget}
              />
            </FlexDiv>
          )}
        </PanelHeader>
      }
      footer={
        showTemplates ? (
          <TemplatesFooter />
        ) : (
          <PanelFooter>
            <Button
              type="secondary"
              text={t("action.close")}
              onClick={onClose}
            />
          </PanelFooter>
        )
      }
    >
      <PanelBody>
        {showTemplates ? (
          <TemplateSelection />
        ) : (
          <WidgetSettings
            key={
              selectedWidget && "workspacePreferenceId" in selectedWidget
                ? selectedWidget.workspacePreferenceId
                : undefined
            }
            widget={selectedWidget}
            onUpdateWidget={onUpdateWidget}
            onUpdateWidgetSize={onUpdateWidgetSize}
          />
        )}
      </PanelBody>
    </InlinePanel>
  );
};
